import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedDate, FormattedMessage } from 'react-intl';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import Loader from '../../components/loader/Loader';

import 'swiper/swiper.scss';
import 'swiper/components/navigation/navigation.scss';
import 'swiper/components/pagination/pagination.scss';

import './ReviewsSwiper.sass';

import ChannelLink from '../../containers/channelUtils/ChannelLink';
import ReviewsBadge from './ReviewsBadge';
import StarRating from './stars/StarRating';
import { fetchReviews } from '../../ducks/reviews';

SwiperCore.use([Navigation, Pagination]);

const SLIDE_LIMIT = 10;
const MIN_PRODUCT_REVIEWS = 5;

function ReviewsSwiper({ themeColor, productCode, productReviews, dispatch }) {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewsCount, setReviewsCount] = useState();
  const [averageRating, setAverageRating] = useState();

  useEffect(() => {
    // In a product context, wait until the page supplies the product's own
    // reviews (shared with the schema.org markup) before deciding what to show.
    if (productCode && productReviews === undefined) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const base = { ratingOrder: 'desc', limit: SLIDE_LIMIT };

    const request =
      productReviews && productReviews.total > MIN_PRODUCT_REVIEWS
        ? Promise.resolve({
            items: productReviews.items,
            total: productReviews.total,
            // Product-specific set: surface the product's own average rating.
            averageRating: productReviews.averageRating,
          })
        : dispatch(fetchReviews(base)).then((allRes) => ({
            items: (allRes.content && allRes.content.items) || [],
            total: allRes.meta && allRes.meta.total,
            // Catalog fallback: leave the badge on its site-wide headline rating.
            averageRating: undefined,
          }));

    request
      .then((res) => {
        if (cancelled) {
          return;
        }
        setReviews(res.items || []);
        setReviewsCount(res.total);
        setAverageRating(res.averageRating != null ? Number(res.averageRating).toFixed(1) : undefined);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [productCode, productReviews]);

  if (loading) {
    return (
      <Fragment>
        <div className="reviews-slider__header">
          <h3 className="reviews-slider__title">
            <FormattedMessage id="section.generic.reviews" />
          </h3>
        </div>

        <Loader slider />
      </Fragment>
    );
  }

  if (!loading && reviews.length <= 0) {
    return null;
  }

  return (
    <Fragment>
      <div className="reviews-slider__header">
        <h3 className="reviews-slider__title">
          <FormattedMessage id="section.generic.reviews" />
        </h3>

        <ChannelLink to="/reviews" className="reviews-slider__more">
          <FormattedMessage id="section.reviews.slider.more" />{' '}
          <span className="reviews-slider__more-count" style={{ color: themeColor }}>
            {reviewsCount}
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 12.5L10 7L4 1.5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ChannelLink>
      </div>

      <div className="reviews-slider__wrapper">
        <ReviewsBadge reviewsCount={reviewsCount} averageRating={averageRating} />

        <Swiper
          pagination={{
            el: '.reviews-slider__pagination',
            clickable: true,
          }}
          navigation={{
            prevEl: '.reviews-slider__prev',
            nextEl: '.reviews-slider__next',
          }}
          breakpoints={{
            320: {
              slidesPerView: 1,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            991: {
              slidesPerView: 3,
              spaceBetween: 20,
            },
            1199: {
              slidesPerView: 4,
              spaceBetween: 20,
            },
          }}
        >
          {reviews.map((review, idx) => (
            <SwiperSlide key={idx} className="reviews-slider__item  reviews-slider__slide">
              <div className="reviews-slider__top">
                <div className="reviews-slider__author">
                  {review.publicName || <FormattedMessage id="section.reviews.anonymous" />}
                </div>

                <div className="reviews-slider__rating">
                  <StarRating themeColor={themeColor} value={review.rating} />
                </div>
              </div>

              <div className="reviews-slider__text">{review.comment}</div>

              {/* Temporarily hidden — restore to show the review date.
              <div className="reviews-slider__date">
                <FormattedDate
                  year="numeric"
                  month="long"
                  day="numeric"
                  value={review.createdAt}
                />
              </div>
              */}
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="reviews-slider__controls">
          <button type="button" className="reviews-slider__swiper-button reviews-slider__prev">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.8571 16.0724L5.14286 9.00096L12.8571 1.92953"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="reviews-slider__pagination"></div>

          <button type="button" className="reviews-slider__swiper-button reviews-slider__next">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.14285 16.0724L12.8571 9.00096L5.14285 1.92953"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </Fragment>
  );
}

export default connect()(ReviewsSwiper);



// WEBPACK FOOTER //
// ./components/reviews/ReviewsSwiper.js